import {
  brandGradFillClass,
  cn,
  ghostAccentIconTileClass,
} from '@up2stay/up2promo-ui';
import { motion, useReducedMotion } from 'motion/react';
import {
  LayoutDashboard,
  type LucideIcon,
  Package,
  Star,
  UserCircle,
} from 'lucide-react';

import { useDictionary } from '@/core/providers/dictionary-provider';
import { useAuthStore } from '@/core/store/auth.store';

import {
  WORKSPACE_INTENT,
  WORKSPACE_INTENTS,
  type WorkspaceIntent,
} from './onboarding-intent';
import { onboardingSoftSpring } from './onboarding-motion';

const INTENT_ICONS: Record<WorkspaceIntent, LucideIcon> = {
  [WORKSPACE_INTENT.profiles]: UserCircle,
  [WORKSPACE_INTENT.touchpoints]: Package,
  [WORKSPACE_INTENT.reputation]: Star,
  [WORKSPACE_INTENT.full]: LayoutDashboard,
};

/** Profiles SoT step title — matches Create Location wizard. */
const STEP_TITLE_CLASS =
  'text-balance text-[22px] font-bold leading-tight text-foreground sm:text-[24px]';

const STEP_BODY_CLASS =
  'text-pretty text-[13.5px] leading-relaxed text-muted-foreground';

const SELECTED_OPTION_CLASS =
  'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] shadow-[var(--shadow-card)] ring-1 ring-[color:var(--brand-500)]/25';

const IDLE_OPTION_CLASS =
  'border-border/80 bg-card/60 hover:border-[color:var(--ghost-accent-border)] hover:bg-[color:var(--ghost-accent-bg)]/40';

interface OnboardingIntentStepProps {
  selected: WorkspaceIntent | null;
  onSelect: (intent: WorkspaceIntent) => void;
  /** First-run only — personal greeting; omitted for create-another. */
  showGreeting?: boolean;
}

export function OnboardingIntentStep({
  selected,
  onSelect,
  showGreeting = false,
}: OnboardingIntentStepProps) {
  const dict = useDictionary();
  const reduceMotion = useReducedMotion();
  const user = useAuthStore((state) => state.user);
  const copy = dict.onboarding.intent;

  const firstName = user?.firstName?.trim() || '';
  const greeting =
    showGreeting && firstName
      ? copy.greeting.replace('{name}', firstName)
      : showGreeting
        ? copy.greetingFallback
        : null;

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
      <div className="flex flex-col gap-2 text-center md:text-start">
        {greeting ? (
          <p className="text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground">
            {greeting}
          </p>
        ) : null}
        <h2 className={STEP_TITLE_CLASS}>{copy.title}</h2>
        <p className={STEP_BODY_CLASS}>{copy.description}</p>
      </div>

      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {WORKSPACE_INTENTS.map((intent, index) => {
          const Icon = INTENT_ICONS[intent];
          const item = copy.options[intent];
          const isSelected = selected === intent;
          return (
            <motion.li
              key={intent}
              initial={reduceMotion ? false : { opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                ...onboardingSoftSpring,
                delay: reduceMotion ? 0 : index * 0.04,
              }}
            >
              <button
                type="button"
                onClick={() => onSelect(intent)}
                aria-pressed={isSelected}
                className={cn(
                  'flex h-full w-full flex-col items-start gap-2 rounded-[var(--r-card)] border px-4 py-3.5 text-start transition-[color,background-color,box-shadow,border-color] duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  isSelected ? SELECTED_OPTION_CLASS : IDLE_OPTION_CLASS,
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-[var(--r-control)]',
                    isSelected ? brandGradFillClass : ghostAccentIconTileClass,
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="text-[13.5px] font-semibold text-foreground">
                  {item.label}
                </span>
                <span className="text-[13.5px] leading-snug text-muted-foreground">
                  {item.description}
                </span>
              </button>
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
}
